import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { BASE_API_URL } from '../helpers/Config';
import { FaSearch, FaCalendarAlt, FaUser, FaLevelUpAlt, FaChartLine, FaMoneyBillAlt, FaExchangeAlt, FaFilter, FaSync } from 'react-icons/fa';

const SubordinateData = ({ theme = 'dark' }) => {
    const isDarkMode = theme === 'dark';
    const [loading, setLoading] = useState(true); // Start with loading true for initial fetch
    const [formData, setFormData] = useState({
        user_id: '',
        from_date: '',
        to_date: '',
        levels: []
    });
    const [stats, setStats] = useState(null);
    const [isInitialFetch, setIsInitialFetch] = useState(true);

    // Level options for react-select with "All Levels" option
    const levelOptions = [
        { value: 'all', label: 'All Levels' },
        { value: 1, label: 'Level 1' },
        { value: 2, label: 'Level 2' },
        { value: 3, label: 'Level 3' },
        { value: 4, label: 'Level 4' },
        { value: 5, label: 'Level 5' },
        { value: 6, label: 'Level 6' }
    ];

    // Custom styles for react-select
    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
            borderColor: isDarkMode ? '#334155' : '#e2e8f0',
            color: isDarkMode ? '#f9fafb' : '#111827',
            borderRadius: '8px',
            fontSize: '14px',
            minHeight: '42px',
            boxShadow: state.isFocused ? (isDarkMode ? '0 0 0 2px rgba(37, 99, 235, 0.3)' : '0 0 0 2px rgba(13, 110, 253, 0.25)') : 'none',
            '&:hover': {
                borderColor: isDarkMode ? '#555' : '#adb5bd'
            }
        }),
        menu: (base) => ({
            ...base,
            backgroundColor: isDarkMode ? '#020617' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#1f2937' : '#e5e7eb'}`,
            borderRadius: '8px',
            zIndex: 9999
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
                ? (isDarkMode ? '#2563eb' : '#0d6efd')
                : state.isFocused
                    ? (isDarkMode ? '#3d3d3d' : '#f8f9fa')
                    : (isDarkMode ? '#2d2d2d' : '#ffffff'),
            color: state.isSelected ? 'white' : (isDarkMode ? '#e5e7eb' : '#2c3e50'),
            '&:hover': {
                backgroundColor: isDarkMode ? '#3d3d3d' : '#f8f9fa'
            }
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: isDarkMode ? '#374151' : '#e9ecef',
            borderRadius: '6px'
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: isDarkMode ? '#e5e7eb' : '#2c3e50',
            fontSize: '12px',
            padding: '4px 8px'
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: isDarkMode ? '#9ca3af' : '#6c757d',
            '&:hover': {
                backgroundColor: isDarkMode ? '#ef4444' : '#dc3545',
                color: 'white'
            }
        }),
        placeholder: (base) => ({
            ...base,
            color: isDarkMode ? '#9ca3af' : '#6c757d'
        }),
        singleValue: (base) => ({
            ...base,
            color: isDarkMode ? '#e5e7eb' : '#2c3e50'
        })
    };

    // Get current date in YYYY-MM-DD format
    const getCurrentDate = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Fetch data function
    const fetchData = async (formDataToFetch) => {
        setLoading(true);
        try {
            const response = await axios.post(`${BASE_API_URL}/team/subordinate-data`, {
                token: localStorage.getItem('a5srf4wer7w1cd') || '',
                from_date: formDataToFetch.from_date,
                to_date: formDataToFetch.to_date,
                levels: formDataToFetch.levels,
                user_id: formDataToFetch.user_id || ''
            });

            const data = response.data;

            if (data.error) {
                toast.error(data.error || 'Failed to fetch data');
                setStats(null);
            } else {
                setStats(data.data);
                if (!isInitialFetch) {
                    toast.success('Data loaded successfully');
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            if (!isInitialFetch) {
                toast.error('Failed to fetch subordinate data');
            }
            setStats(null);
        } finally {
            setLoading(false);
            setIsInitialFetch(false);
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.from_date) {
            toast.error('Please select From Date');
            return;
        }

        if (!formData.to_date) {
            toast.error('Please select To Date');
            return;
        }

        if (formData.levels.length === 0) {
            toast.error('Please select at least one level');
            return;
        }

        fetchData(formData);
    };

    // Handle level selection change with mutual exclusivity for "All Levels"
    const handleLevelChange = (selectedOptions) => {
        if (!selectedOptions || selectedOptions.length === 0) {
            setFormData(prev => ({ ...prev, levels: [] }));
            return;
        }

        const selectedValues = selectedOptions.map(opt => opt.value);
        const lastSelected = selectedValues[selectedValues.length - 1];

        // Check if "All Levels" was selected
        if (lastSelected === 'all') {
            // If "All Levels" is selected, clear all other selections and select all 6 levels
            setFormData(prev => ({ ...prev, levels: [1, 2, 3, 4, 5, 6] }));
        }
        // Check if a specific level was selected when "All Levels" was previously selected
        else if (formData.levels.length === 6 && lastSelected !== 'all') {
            // If all levels were selected and user selects a specific level,
            // clear all levels and select only that specific level
            const numericValue = Number(lastSelected);
            setFormData(prev => ({ ...prev, levels: [numericValue] }));
        }
        // Normal selection/deselection of individual levels
        else {
            // Filter out 'all' from values and ensure only numbers
            const numericValues = selectedValues
                .filter(val => val !== 'all')
                .map(val => Number(val));
            setFormData(prev => ({ ...prev, levels: numericValues }));
        }
    };

    // Get selected options for react-select
    const getSelectedOptions = () => {
        if (formData.levels.length === 6) {
            return levelOptions.filter(opt => opt.value === 'all');
        }
        return levelOptions.filter(opt => formData.levels.includes(opt.value));
    };

    // Set default dates (last 7 days)
    const setDefaultDates = () => {
        const today = new Date();
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);

        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        setFormData(prev => ({
            ...prev,
            from_date: formatDate(weekAgo),
            to_date: formatDate(today)
        }));
    };

    // Reset form to default (today's date and all levels)
    const handleReset = () => {
        const today = getCurrentDate();
        const defaultFormData = {
            user_id: '',
            from_date: today,
            to_date: today,
            levels: [1, 2, 3, 4, 5, 6] // All levels selected
        };
        setFormData(defaultFormData);
        setStats(null);
        setIsInitialFetch(false);
    };

    // Quick date handlers
    const setToday = () => {
        const today = getCurrentDate();
        setFormData(prev => ({
            ...prev,
            from_date: today,
            to_date: today
        }));
    };

    const setThisMonth = () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        setFormData(prev => ({
            ...prev,
            from_date: formatDate(firstDay),
            to_date: getCurrentDate()
        }));
    };

    // Initialize with default dates and fetch data on page load
    useEffect(() => {
        const today = getCurrentDate();
        const defaultFormData = {
            user_id: '',
            from_date: today,
            to_date: today,
            levels: [1, 2, 3, 4, 5, 6] // All levels selected by default
        };
        setFormData(defaultFormData);

        // Fetch data immediately on page load
        fetchData(defaultFormData);
    }, []);

    // Format number with commas (full value, not compact)
    const formatNumber = (num) => {
        if (num === null || num === undefined) return '0';
        return new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(num);
    };

    const pageStyles = {
        minHeight: '100vh',
        padding: '12px',
        fontSize: '14px',
        maxWidth: '480px',
        margin: '0 auto',
        backgroundColor: isDarkMode ? '#111827' : '#f8fafc',
        color: isDarkMode ? '#f9fafb' : '#111827',
        transition: 'background 0.25s ease, color 0.25s ease'
    };

    const cardStyles = {
        backgroundColor: isDarkMode ? '#020617' : '#ffffff',
        border: `1px solid ${isDarkMode ? '#1f2937' : '#e5e7eb'}`,
        borderRadius: '14px',
        boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.35)' : '0 4px 16px rgba(15, 23, 42, 0.06)'
    };

    const inputStyles = {
        backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc',
        color: isDarkMode ? '#f9fafb' : '#111827',
        border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
        borderRadius: '10px',
        fontSize: '14px',
        height: '42px'
    };

    const buttonPrimary = {
        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        padding: '10px 16px',
        fontWeight: '600',
        fontSize: '14px'
    };

    const buttonSecondary = {
        backgroundColor: isDarkMode ? '#374151' : '#6c757d',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        padding: '10px 16px',
        fontWeight: '600',
        fontSize: '14px'
    };

    const statCardStyles = {
        backgroundColor: isDarkMode ? '#2d2d2d' : '#ffffff',
        border: `1px solid ${isDarkMode ? '#3d3d3d' : '#e9ecef'}`,
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '12px'
    };

    const quickDateButtonStyle = {
        backgroundColor: isDarkMode ? '#2d2d2d' : '#f8f9fa',
        color: isDarkMode ? '#e5e7eb' : '#495057',
        border: `1px solid ${isDarkMode ? '#444' : '#dee2e6'}`,
        fontSize: '13px',
        padding: '6px 12px',
        borderRadius: '4px',
        margin: '2px'
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="subordinate-stats-page"
            style={pageStyles}
        >
            <style>
                {`
                    /* Custom scrollbar */
                    ::-webkit-scrollbar {
                        width: 4px;
                    }
                    ::-webkit-scrollbar-track {
                        background: ${isDarkMode ? '#2d2d2d' : '#f1f1f1'};
                    }
                    ::-webkit-scrollbar-thumb {
                        background: ${isDarkMode ? '#555' : '#888'};
                        border-radius: 2px;
                    }
                    ::-webkit-scrollbar-thumb:hover {
                        background: ${isDarkMode ? '#777' : '#555'};
                    }
                    
                    /* Loading animation */
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    
                    .loading-spinner {
                        animation: spin 1s linear infinite;
                    }
                    
                    /* Stat card hover effect */
                    .stat-card:hover {
                        transform: translateY(-2px);
                        transition: transform 0.2s ease;
                    }
                    
                    /* Responsive adjustments */
                    @media (min-width: 768px) {
                        .stat-grid {
                            display: grid;
                            grid-template-columns: repeat(1, 1fr);
                            gap: 12px;
                        }
                    }
                `}
            </style>

            <div className="container-fluid px-3 py-3">
                {/* Header */}
                <div className="mb-4">
                    <h1 className="h4 fw-bold mb-2 d-flex align-items-center gap-2" style={{ color: isDarkMode ? '#ffffff' : '#2c3e50' }}>
                        <FaFilter size={18} />
                        Subordinate Statistics
                    </h1>
                    <p className="small mb-0" style={{ color: isDarkMode ? '#a0a0a0' : '#6c757d' }}>
                        View team member deposit and trade statistics
                    </p>
                </div>

                {/* Filter Form Card - Full Width */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    style={cardStyles}
                    className="mb-4"
                >
                    <div className="p-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                                <FaFilter size={16} />
                                Filters
                            </h6>
                            <button
                                type="button"
                                className="btn btn-sm d-flex align-items-center gap-1"
                                onClick={handleReset}
                                style={{
                                    backgroundColor: isDarkMode ? '#2d2d2d' : '#f8f9fa',
                                    color: isDarkMode ? '#e5e7eb' : '#495057',
                                    border: `1px solid ${isDarkMode ? '#444' : '#dee2e6'}`,
                                    fontSize: '13px',
                                    padding: '6px 12px',
                                    borderRadius: '4px'
                                }}
                            >
                                <FaSync size={12} />
                                Reset
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="row g-3">
                                {/* User ID */}
                                <div className="col-12">
                                    <label className="form-label d-flex align-items-center gap-2 mb-2 small fw-semibold">
                                        <FaUser size={14} />
                                        User ID (Optional)
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter user ID"
                                        value={formData.user_id}
                                        onChange={(e) => setFormData(prev => ({ ...prev, user_id: e.target.value }))}
                                        style={inputStyles}
                                    />
                                </div>

                                {/* Date Range */}
                                <div className="col-12">
                                    <label className="form-label d-flex align-items-center gap-2 mb-2 small fw-semibold">
                                        <FaCalendarAlt size={14} />
                                        Date Range
                                    </label>
                                    <div className="row g-2 mb-3">
                                        <div className="col-12 col-md-6">
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={formData.from_date}
                                                onChange={(e) => setFormData(prev => ({ ...prev, from_date: e.target.value }))}
                                                style={inputStyles}
                                                max={getCurrentDate()}
                                                required
                                            />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={formData.to_date}
                                                onChange={(e) => setFormData(prev => ({ ...prev, to_date: e.target.value }))}
                                                style={inputStyles}
                                                max={getCurrentDate()}
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Quick Dates */}
                                    <div className="mb-3">
                                        <div className="small text-muted mb-2">Quick Select:</div>
                                        <div className="d-flex flex-wrap">
                                            <button
                                                type="button"
                                                className="btn btn-sm"
                                                onClick={setToday}
                                                style={quickDateButtonStyle}
                                            >
                                                Today
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm"
                                                onClick={setDefaultDates}
                                                style={quickDateButtonStyle}
                                            >
                                                Last 7 Days
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm"
                                                onClick={setThisMonth}
                                                style={quickDateButtonStyle}
                                            >
                                                This Month
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Levels Selection */}
                                <div className="col-12">
                                    <label className="form-label d-flex align-items-center gap-2 mb-2 small fw-semibold">
                                        <FaLevelUpAlt size={14} />
                                        Levels
                                    </label>
                                    <Select
                                        isMulti
                                        options={levelOptions}
                                        value={getSelectedOptions()}
                                        onChange={handleLevelChange}
                                        styles={customSelectStyles}
                                        placeholder="Select levels..."
                                        className="basic-multi-select"
                                        classNamePrefix="select"
                                    />
                                    <div className="form-text small mt-1" style={{ color: isDarkMode ? '#a0a0a0' : '#6c757d' }}>
                                        Select "All Levels" for all 6 levels or choose specific levels
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="col-12">
                                    <button
                                        type="submit"
                                        className="btn w-100 d-flex align-items-center justify-content-center gap-2"
                                        style={buttonPrimary}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="loading-spinner">⟳</span>
                                                {isInitialFetch ? 'Loading...' : 'Fetching Data...'}
                                            </>
                                        ) : (
                                            <>
                                                <FaSearch size={16} />
                                                Get Statistics
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </motion.div>

                {/* Statistics Display - Full Width */}
                {loading && isInitialFetch ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        style={cardStyles}
                        className="text-center py-5"
                    >
                        <div className="mb-3">
                            <span className="loading-spinner" style={{ fontSize: '40px', color: isDarkMode ? '#3b82f6' : '#0d6efd' }}>⟳</span>
                        </div>
                        <h5 className="h6 mb-2" style={{ color: isDarkMode ? '#a0a0a0' : '#6c757d' }}>
                            Loading Statistics...
                        </h5>
                        <p className="small mb-0" style={{ color: isDarkMode ? '#888' : '#adb5bd' }}>
                            Fetching data for today with all levels
                        </p>
                    </motion.div>
                ) : stats ? (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                        className="stats-container"
                    >
                        {/* Deposit Statistics - Full Width Grid */}
                        <div className="mb-4">
                            <h5 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                <FaMoneyBillAlt size={18} />
                                Deposit Statistics
                            </h5>

                            <div className="stat-grid">
                                {/* First Deposit */}
                                <div className="stat-card" style={statCardStyles}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="small text-muted">First Deposit</div>
                                        <div className="badge" style={{
                                            backgroundColor: isDarkMode ? '#10b98120' : '#19875420',
                                            color: isDarkMode ? '#10b981' : '#198754',
                                            fontSize: '12px',
                                            padding: '4px 8px',
                                            borderRadius: '4px'
                                        }}>
                                            Count
                                        </div>
                                    </div>
                                    <div className="h3 fw-bold mb-0" style={{ color: isDarkMode ? '#10b981' : '#198754' }}>
                                        {stats.deposit?.first_deposit?.count || 0}
                                    </div>
                                    <div className="mt-3">
                                        <div className="small text-muted mb-1">Coin</div>
                                        <div className="h5 fw-bold" style={{ color: isDarkMode ? '#10b981' : '#198754' }}>
                                            {formatNumber(stats.deposit?.first_deposit?.amount || 0)}
                                        </div>
                                    </div>
                                </div>

                                {/* Normal Deposit */}
                                <div className="stat-card" style={statCardStyles}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="small text-muted">Normal Deposit</div>
                                        <div className="badge" style={{
                                            backgroundColor: isDarkMode ? '#3b82f620' : '#0d6efd20',
                                            color: isDarkMode ? '#3b82f6' : '#0d6efd',
                                            fontSize: '12px',
                                            padding: '4px 8px',
                                            borderRadius: '4px'
                                        }}>
                                            Count
                                        </div>
                                    </div>
                                    <div className="h3 fw-bold mb-0" style={{ color: isDarkMode ? '#3b82f6' : '#0d6efd' }}>
                                        {stats.deposit?.normal_deposit?.count || 0}
                                    </div>
                                    <div className="mt-3">
                                        <div className="small text-muted mb-1">Coin</div>
                                        <div className="h5 fw-bold" style={{ color: isDarkMode ? '#3b82f6' : '#0d6efd' }}>
                                            {formatNumber(stats.deposit?.normal_deposit?.amount || 0)}
                                        </div>
                                    </div>
                                </div>

                                {/* Total Deposit */}
                                <div className="stat-card" style={{
                                    ...statCardStyles,
                                    backgroundColor: isDarkMode ? '#1e3a8a' : '#0d6efd',
                                    color: 'white'
                                }}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="small">Total Deposit</div>
                                        <div className="badge" style={{
                                            backgroundColor: 'rgba(255,255,255,0.2)',
                                            color: 'white',
                                            fontSize: '12px',
                                            padding: '4px 8px',
                                            borderRadius: '4px'
                                        }}>
                                            Total
                                        </div>
                                    </div>
                                    <div className="h3 fw-bold mb-0">
                                        {(stats.deposit?.first_deposit?.count || 0) + (stats.deposit?.normal_deposit?.count || 0)}
                                    </div>
                                    <div className="mt-3">
                                        <div className="small mb-1 opacity-75">Total Coin</div>
                                        <div className="h5 fw-bold">
                                            {formatNumber(
                                                (stats.deposit?.first_deposit?.amount || 0) +
                                                (stats.deposit?.normal_deposit?.amount || 0)
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Trade Statistics - Full Width Grid */}
                        <div className="mb-4">
                            <h5 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                <FaExchangeAlt size={18} />
                                Trade Statistics
                            </h5>

                            <div className="stat-grid">
                                {/* Trade Count */}
                                <div className="stat-card" style={statCardStyles}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="small text-muted">Trade Count</div>
                                        <FaExchangeAlt size={16} style={{ color: isDarkMode ? '#8b5cf6' : '#6f42c1', opacity: 0.7 }} />
                                    </div>
                                    <div className="h3 fw-bold mb-0" style={{ color: isDarkMode ? '#8b5cf6' : '#6f42c1' }}>
                                        {stats.trade?.count || 0}
                                    </div>
                                    <div className="mt-3">
                                        <div className="small text-muted mb-1">Total Trades</div>
                                        <div className="small fw-semibold">
                                            All trade activities
                                        </div>
                                    </div>
                                </div>

                                {/* Trade Amount */}
                                <div className="stat-card" style={statCardStyles}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="small text-muted">Trade Coin</div>
                                        <div className="badge" style={{
                                            backgroundColor: isDarkMode ? '#8b5cf620' : '#6f42c120',
                                            color: isDarkMode ? '#8b5cf6' : '#6f42c1',
                                            fontSize: '12px',
                                            padding: '4px 8px',
                                            borderRadius: '4px'
                                        }}>
                                            Value
                                        </div>
                                    </div>
                                    <div className="h3 fw-bold mb-0" style={{ color: isDarkMode ? '#8b5cf6' : '#6f42c1' }}>
                                        {formatNumber(stats.trade?.amount || 0)}
                                    </div>
                                    <div className="mt-3">
                                        <div className="small text-muted mb-1">Total Trading Volume</div>
                                    </div>
                                </div>

                                {/* Average per Trade */}
                                <div className="stat-card" style={{
                                    ...statCardStyles,
                                    borderLeft: `4px solid ${isDarkMode ? '#8b5cf6' : '#6f42c1'}`
                                }}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="small text-muted">Average Coin Per Trade</div>
                                        <FaChartLine size={18} style={{ color: isDarkMode ? '#8b5cf6' : '#6f42c1', opacity: 0.7 }} />
                                    </div>
                                    <div className="h3 fw-bold mb-0" style={{ color: isDarkMode ? '#8b5cf6' : '#6f42c1' }}>
                                        {stats.trade?.count && stats.trade.count > 0 ?
                                            formatNumber(stats.trade.amount / stats.trade.count) : '0.00'}
                                    </div>
                                    <div className="mt-3">
                                        <div className="small text-muted mb-1">Per Transaction Average</div>
                                        <div className="small fw-semibold">
                                            {stats.trade?.count || 0} trades
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                ) : !loading && !stats ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        style={cardStyles}
                        className="text-center py-5"
                    >
                        <div className="mb-3">
                            <FaChartLine size={48} style={{ color: isDarkMode ? '#555' : '#ccc', opacity: 0.5 }} />
                        </div>
                        <h5 className="h6 mb-2" style={{ color: isDarkMode ? '#a0a0a0' : '#6c757d' }}>
                            No Data Available
                        </h5>
                        <p className="small mb-0" style={{ color: isDarkMode ? '#888' : '#adb5bd' }}>
                            No statistics found for the selected filters
                        </p>
                    </motion.div>
                ) : null}
            </div>
        </motion.div>
    );
};

export default SubordinateData;